import type { AxiosInstance, AxiosResponse } from 'axios';
import { IVFSResponse } from './types';

/**
 * Base API class that all API classes should extend
 * Provides common functionality and enforces IVFSResponse return types
 */
abstract class BaseAPI {
  protected readonly apiInstance: AxiosInstance;

  constructor(api: AxiosInstance) {
    this.apiInstance = api;
  }

  /**
   * Wraps API responses in the IVFSResponse format
   * @param options - Options for the wrapResponse method
   * @param options.promise - The promise returned by an API call
   * @param options.errorMessage - Default error message if the API call fails
   */
  protected async wrapResponse<T>({
    promise,
    errorMessage,
  }: {
    promise: Promise<AxiosResponse<any>>;
    errorMessage: string;
  }): Promise<IVFSResponse<T>> {
    try {
      const response = await promise;

      // If the response is already in IVFSResponse format, return it
      if (
        response?.data &&
        typeof response.data === 'object' &&
        'success' in response.data
      ) {
        return response.data;
      }

      // Otherwise, wrap it in IVFSResponse format
      return {
        success: true,
        message: response?.data?.message || '',
        data: response?.data?.data || ({} as T),
      };
    } catch (error: any) {
      const message = error.message || errorMessage;
      throw new Error(message);
    }
  }
}

export default BaseAPI;
